/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2022, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';

import {
  selectTargetEnvironmentId,
  selectEnvironmentById
} from '@ibm-watson/shared/modules/ia/store/environments/selectors';
import { useAppSelector } from '@ibm-watson/shared/store/';
import { useChannelOverlayContext } from '../../../hooks/useChannelOverlayContext';
import { QuickSetupComponentProps } from '../../../util/types';
import { QuickSetupProvider } from '../../common/quick-setup/QuickSetupProvider';
import { Step } from '../../common/quick-setup/Step';
import { ConnectStep } from '../connect-step-tab/ConnectStep';
import { ReviewConfirm } from '../review-confirm-tab/ReviewConfirm';
import { SelectEvents } from '../select-events-tab/SelectEvents';
import { SegmentQuickSetup as messages } from './messages';

export const SegmentQuickSetup = ({
  environmentId,
  readOnly
}: QuickSetupComponentProps) => {
  const { formatMessage: t } = useIntl();

  const isLive = useAppSelector(state => {
    const liveEnvironmentId = selectTargetEnvironmentId(state);
    return liveEnvironmentId === environmentId;
  });
  const { environmentName } = useAppSelector(state => {
    const environment = selectEnvironmentById(state, environmentId);
    return {
      environmentName: environment?.name
    };
  });

  const { buttonHooks, setShowNextBack } = useChannelOverlayContext();

  useEffect(() => {
    setShowNextBack(true);
  }, [setShowNextBack]);

  return (
    <>
      <QuickSetupProvider readOnly={readOnly} buttonHooks={buttonHooks}>
        <Step label={t(messages.connect)} required>
          <ConnectStep isLive={isLive} readOnly={readOnly} />
        </Step>
        <Step secondaryLabel={t(messages.selectEvents)}>
          <SelectEvents isLive={isLive} readOnly={readOnly} />
        </Step>
        <Step secondaryLabel={t(messages.reviewAndConfirm)}>
          <ReviewConfirm isLive={isLive} envName={environmentName} />
        </Step>
      </QuickSetupProvider>
    </>
  );
};
